/**
 * services/quant/expectancyMatrix.ts
 *
 * Setup Expectancy Matrix — MER Algo Footprints v5.3
 *
 * Measures contextual edge by setup × regime × phase interaction.
 * Answers: which setup families work best in which regime and phase?
 *
 * Pure calculation — no engine changes, no execution logic.
 */

import { AuditRecord } from '../../../../types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ExpectancyMatrixCell {
  setupType:   string;
  regime:      string;
  phase:       string;
  count:       number;
  winRate:     number | null;
  avgR:        number | null;
  medianR:     number | null;
  expectancy:  number | null;
  avgMFE:      number | null;
  avgMAE:      number | null;
  quality:     'high' | 'medium' | 'low' | 'insufficient_data';
}

export interface ExpectancyMatrixSummary {
  cells:      ExpectancyMatrixCell[];
  bestCells:  ExpectancyMatrixCell[];   // top 5 by expectancy (min sample met)
  worstCells: ExpectancyMatrixCell[];   // bottom 5 by expectancy (min sample met)
  totalCells: number;
  cellsWithData: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MIN_SAMPLE = 3;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function safeWinRate(recs: AuditRecord[]): number | null {
  const decisive = recs.filter(r => r.outcome === 'win' || r.outcome === 'loss');
  if (!decisive.length) return null;
  return decisive.filter(r => r.outcome === 'win').length / decisive.length;
}

function safeAvgR(recs: AuditRecord[]): number | null {
  const withR = recs.filter(r => r.realizedR !== null);
  if (!withR.length) return null;
  return withR.reduce((s, r) => s + r.realizedR!, 0) / withR.length;
}

function safeMedianR(recs: AuditRecord[]): number | null {
  const rs = recs.filter(r => r.realizedR !== null).map(r => r.realizedR!).sort((a, b) => a - b);
  if (!rs.length) return null;
  const mid = Math.floor(rs.length / 2);
  return rs.length % 2 !== 0 ? rs[mid] : (rs[mid - 1] + rs[mid]) / 2;
}

function safeExpectancy(recs: AuditRecord[]): number | null {
  const wr = safeWinRate(recs);
  if (wr === null) return null;
  const wins   = recs.filter(r => r.outcome === 'win').map(r => r.realizedR ?? 0);
  const losses = recs.filter(r => r.outcome === 'loss').map(r => r.realizedR ?? 0);
  if (!wins.length || !losses.length) return safeAvgR(recs);
  const avgWin  = wins.reduce((s, v) => s + v, 0) / wins.length;
  const avgLoss = losses.reduce((s, v) => s + v, 0) / losses.length;
  return wr * avgWin + (1 - wr) * avgLoss;
}

function safeAvgField(recs: AuditRecord[], field: 'mfe' | 'mae'): number | null {
  const withVal = recs.filter(r => r[field] !== null);
  if (!withVal.length) return null;
  return withVal.reduce((s, r) => s + r[field]!, 0) / withVal.length;
}

function cellQuality(
  count: number,
  expectancy: number | null,
  winRate: number | null,
): ExpectancyMatrixCell['quality'] {
  if (count < MIN_SAMPLE || expectancy === null) return 'insufficient_data';
  // Quality is driven primarily by expectancy, with win-rate as a tie-breaker
  if (expectancy >= 0.5 && (winRate ?? 0) >= 0.5) return 'high';
  if (expectancy >= 0.0) return 'medium';
  return 'low';
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function computeExpectancyMatrix(
  records: AuditRecord[],
): ExpectancyMatrixSummary {
  const closed = records.filter(r => r.status === 'closed' && r.realizedR !== null);

  // Group by setupType × regime × phase
  const groups = new Map<string, AuditRecord[]>();
  for (const r of closed) {
    const key = `${r.setupType}||${r.regime}||${r.phase}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(r);
  }

  const cells: ExpectancyMatrixCell[] = [];
  for (const [key, recs] of groups.entries()) {
    const [setupType, regime, phase] = key.split('||');
    const expectancy = safeExpectancy(recs);
    const winRate    = safeWinRate(recs);
    cells.push({
      setupType,
      regime,
      phase,
      count:       recs.length,
      winRate,
      avgR:        safeAvgR(recs),
      medianR:     safeMedianR(recs),
      expectancy,
      avgMFE:      safeAvgField(recs, 'mfe'),
      avgMAE:      safeAvgField(recs, 'mae'),
      quality:     cellQuality(recs.length, expectancy, winRate),
    });
  }

  // Sort overall by expectancy desc (null last)
  cells.sort((a, b) => {
    if (a.expectancy === null && b.expectancy === null) return 0;
    if (a.expectancy === null) return 1;
    if (b.expectancy === null) return -1;
    return b.expectancy - a.expectancy;
  });

  const qualified = cells.filter(c => c.quality !== 'insufficient_data');

  // Sample-weighted ranking: adjustedExpectancy = expectancy × weight(count)
  // Used ONLY for selecting bestCells / worstCells — raw expectancy is unchanged in display.
  function expectancyWeight(count: number): number {
    return Math.min(1, count / 10);
  }
  const rankedDesc = [...qualified].sort((a, b) => {
    const wa = (a.expectancy ?? 0) * expectancyWeight(a.count);
    const wb = (b.expectancy ?? 0) * expectancyWeight(b.count);
    return wb - wa;
  });
  const rankedAsc = [...qualified].sort((a, b) => {
    const wa = (a.expectancy ?? 0) * expectancyWeight(a.count);
    const wb = (b.expectancy ?? 0) * expectancyWeight(b.count);
    return wa - wb;
  });

  const bestCells  = rankedDesc.slice(0, 5);
  const worstCells = rankedAsc.slice(0, 5);

  return {
    cells,
    bestCells,
    worstCells,
    totalCells:    cells.length,
    cellsWithData: qualified.length,
  };
}
